import { useState, useEffect } from 'react';
import axios from 'axios';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../components/ui/table';
import { Globe, Plus, Clock, Loader2, Check, X } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const LANGUAGE_MAP = {
    en: 'English', fr: 'French', es: 'Spanish', de: 'German', it: 'Italian',
    pt: 'Portuguese', ja: 'Japanese', zh: 'Chinese', ko: 'Korean', ar: 'Arabic',
    ru: 'Russian', nl: 'Dutch', sv: 'Swedish', da: 'Danish', no: 'Norwegian',
    fi: 'Finnish', pl: 'Polish', cs: 'Czech', tr: 'Turkish', hi: 'Hindi'
};

const STATUS_STYLES = {
    pending: 'bg-amber-500/10 text-amber-400',
    in_progress: 'bg-blue-500/10 text-blue-400',
    completed: 'bg-emerald-500/10 text-emerald-400',
    rejected: 'bg-red-500/10 text-red-400'
};

export default function OperatorLanguages() {
    const [requests, setRequests] = useState([]);
    const [attractions, setAttractions] = useState([]);
    const [pricing, setPricing] = useState({ default_price_per_language: 50, language_prices: {} });
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedAttraction, setSelectedAttraction] = useState('');
    const [selectedLanguage, setSelectedLanguage] = useState('');
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        fetchAll();
    }, []);

    const fetchAll = async () => {
        setLoading(true);
        try {
            const [reqRes, attrRes, priceRes] = await Promise.all([
                axios.get(`${API}/api/operator/language-requests`, { withCredentials: true }),
                axios.get(`${API}/api/operator/attractions`, { withCredentials: true }).catch(() => ({ data: [] })),
                axios.get(`${API}/api/language-pricing`).catch(() => ({ data: { default_price_per_language: 50, language_prices: {} } }))
            ]);
            setRequests(reqRes.data);
            setAttractions(attrRes.data);
            setPricing(priceRes.data);
        } catch {
            toast.error('Failed to load data');
        } finally {
            setLoading(false);
        }
    };

    const getPrice = (lang) => {
        return pricing.language_prices?.[lang] || pricing.default_price_per_language || 50;
    };

    const getAvailableLanguages = () => {
        if (!selectedAttraction) return [];
        const attr = attractions.find(a => a.id === selectedAttraction);
        const existing = attr?.languages || [];
        const pendingLangs = requests
            .filter(r => r.attraction_id === selectedAttraction && ['pending', 'in_progress'].includes(r.status))
            .map(r => r.language);
        return Object.entries(LANGUAGE_MAP)
            .filter(([code]) => !existing.includes(code) && !pendingLangs.includes(code))
            .map(([code, name]) => ({ code, name }));
    };

    const handleSubmit = async () => {
        if (!selectedAttraction || !selectedLanguage) return;
        setSubmitting(true);
        try {
            const res = await axios.post(`${API}/api/operator/language-requests`, {
                attraction_id: selectedAttraction,
                language: selectedLanguage
            }, { withCredentials: true });
            toast.success(res.data.message);
            setDialogOpen(false);
            setSelectedAttraction('');
            setSelectedLanguage('');
            fetchAll();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to submit request');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <DashboardLayout type="operator">
            <div className="space-y-8 max-w-5xl">
                <div className="flex items-center justify-between">
                    <div>
                        <h2 className="font-outfit text-2xl font-semibold text-white">Language Add-ons</h2>
                        <p className="text-muted-foreground text-sm mt-1">Request additional languages for your attractions</p>
                    </div>
                    <Button onClick={() => setDialogOpen(true)} className="bg-gold text-[#0A0D14] hover:bg-gold/90 gap-2" data-testid="request-language-btn">
                        <Plus className="w-4 h-4" /> Request Language
                    </Button>
                </div>

                {/* Info card */}
                <div className="p-4 rounded-xl bg-blue-500/5 border border-blue-500/20 flex items-start gap-3">
                    <Clock className="w-5 h-5 text-blue-400 mt-0.5 shrink-0" />
                    <div>
                        <p className="text-sm text-white font-medium">Lead Time: 3-6 weeks per attraction</p>
                        <p className="text-xs text-muted-foreground">New language content requires professional recording and production. You'll be notified when it's ready.</p>
                    </div>
                </div>

                {/* Requests table */}
                {loading ? (
                    <div className="flex items-center justify-center h-32">
                        <Loader2 className="w-6 h-6 animate-spin text-gold" />
                    </div>
                ) : requests.length === 0 ? (
                    <div className="text-center py-12 bg-[#121620] rounded-xl border border-white/10">
                        <Globe className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                        <p className="text-muted-foreground">No language requests yet</p>
                        <p className="text-xs text-white/30 mt-1">Request a new language to expand your audio guides</p>
                    </div>
                ) : (
                    <Table>
                        <TableHeader>
                            <TableRow className="border-white/10 hover:bg-transparent">
                                <TableHead className="text-muted-foreground">Attraction</TableHead>
                                <TableHead className="text-muted-foreground">Language</TableHead>
                                <TableHead className="text-muted-foreground">Cost</TableHead>
                                <TableHead className="text-muted-foreground">Status</TableHead>
                                <TableHead className="text-muted-foreground">Requested</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {requests.map((req) => (
                                <TableRow key={req.id} className="border-white/10 hover:bg-white/5">
                                    <TableCell className="text-white">{req.attraction_title}</TableCell>
                                    <TableCell>
                                        <span className="px-2 py-1 rounded-full text-xs bg-gold/10 text-gold uppercase">
                                            {LANGUAGE_MAP[req.language] || req.language}
                                        </span>
                                    </TableCell>
                                    <TableCell className="text-gold">{req.price_paid} credits</TableCell>
                                    <TableCell>
                                        <span className={`px-2 py-1 rounded-full text-xs capitalize ${STATUS_STYLES[req.status] || 'bg-white/10 text-white'}`}>
                                            {req.status?.replace('_', ' ')}
                                        </span>
                                    </TableCell>
                                    <TableCell className="text-muted-foreground text-xs">
                                        {new Date(req.created_at).toLocaleDateString()}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}

                {/* Request Dialog */}
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <DialogContent className="bg-[#121620] border-white/10 max-w-md">
                        <DialogHeader>
                            <DialogTitle className="text-white font-outfit flex items-center gap-2">
                                <Globe className="w-5 h-5 text-gold" /> Request Language Add-on
                            </DialogTitle>
                            <DialogDescription className="text-muted-foreground text-sm">
                                Select an attraction and the language you need. Credits will be deducted immediately.
                            </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 mt-2">
                            <div className="space-y-2">
                                <Label>Attraction</Label>
                                <Select value={selectedAttraction} onValueChange={(v) => { setSelectedAttraction(v); setSelectedLanguage(''); }}>
                                    <SelectTrigger className="bg-[#0A0D14] border-white/10" data-testid="lang-attraction-select">
                                        <SelectValue placeholder="Select attraction" />
                                    </SelectTrigger>
                                    <SelectContent className="bg-[#121620] border-white/10">
                                        {attractions.map(attr => (
                                            <SelectItem key={attr.id} value={attr.id}>{attr.title}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            {selectedAttraction && (
                                <div className="space-y-2">
                                    <Label>Language</Label>
                                    {getAvailableLanguages().length === 0 ? (
                                        <p className="text-sm text-muted-foreground p-3 rounded-md bg-[#0A0D14] border border-white/10">
                                            All languages already available or requested for this attraction.
                                        </p>
                                    ) : (
                                        <Select value={selectedLanguage} onValueChange={setSelectedLanguage}>
                                            <SelectTrigger className="bg-[#0A0D14] border-white/10" data-testid="lang-language-select">
                                                <SelectValue placeholder="Select language" />
                                            </SelectTrigger>
                                            <SelectContent className="bg-[#121620] border-white/10 max-h-60">
                                                {getAvailableLanguages().map(lang => (
                                                    <SelectItem key={lang.code} value={lang.code}>
                                                        {lang.name} — {getPrice(lang.code)} credits
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    )}
                                </div>
                            )}
                            {selectedLanguage && (
                                <div className="p-3 rounded-md bg-gold/5 border border-gold/20">
                                    <div className="flex justify-between">
                                        <span className="text-sm text-muted-foreground">Cost</span>
                                        <span className="text-gold font-semibold">{getPrice(selectedLanguage)} credits</span>
                                    </div>
                                    <p className="text-xs text-white/30 mt-1">Lead time: 3-6 weeks</p>
                                </div>
                            )}
                            <div className="flex justify-end gap-3 pt-2">
                                <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-white/10">Cancel</Button>
                                <Button
                                    onClick={handleSubmit}
                                    disabled={submitting || !selectedAttraction || !selectedLanguage}
                                    className="bg-gold text-[#0A0D14] hover:bg-gold/90 gap-2"
                                    data-testid="submit-language-request-btn"
                                >
                                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                                    Request Language
                                </Button>
                            </div>
                        </div>
                    </DialogContent>
                </Dialog>
            </div>
        </DashboardLayout>
    );
}
